/**
 * Native (Capacitor) runtime helpers.
 *
 * On web, token helpers no-op — web authentication relies solely on the
 * httpOnly cookie set by the backend. localStorage is NOT used; using it
 * would expose the session token to JavaScript and undermine cookie security.
 *
 * On native (Capacitor), we use `@capacitor/preferences` (encrypted on iOS,
 * SharedPreferences on Android) to persist the bearer token across cold-starts.
 * Cookies don't survive in WebView reliably across all Android OEM browsers,
 * so we explicitly carry the session token in native storage.
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const TOKEN_KEY = 'hourtrix.session_token';

export function isNative() {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

export async function getStoredToken() {
  if (!isNative()) return null; // web uses cookies
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    return value || null;
  } catch (e) {
    return null;
  }
}

export async function setStoredToken(token) {
  if (!isNative()) return; // web uses cookies
  if (!token) return;
  try {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch (e) {
    // ignore — best-effort
  }
}

export async function clearStoredToken() {
  if (!isNative()) return; // web uses cookies
  try {
    await Preferences.remove({ key: TOKEN_KEY });
  } catch (e) {
    // ignore
  }
}

/**
 * Open an external URL in the most appropriate way for the current
 * platform.
 *  - Native (Capacitor): @capacitor/browser opens an in-app Custom Tab
 *    (Chrome on Android, SFSafariViewController on iOS), which lets the
 *    user complete the external flow (e.g. Stripe Customer Portal,
 *    Terms / Privacy / Support links) without being trapped inside the
 *    app's WebView. Closing the tab returns to Hourtrix.
 *  - Web: full-page redirect (or new-tab navigation by the caller).
 *
 * Falls back to `window.location.href = url` if the Capacitor browser
 * plugin fails to load for any reason.
 */
export async function openExternal(url) {
  if (!url) return;
  if (isNative()) {
    try {
      // Lazy-load so the Capacitor browser SDK doesn't bloat the web build.
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url });
      return;
    } catch (e) {
      // Fall back to redirect if the plugin fails for any reason.
    }
  }
  window.location.href = url;
}
